const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const IS_MOBILE = window.matchMedia('(max-width: 749px)').matches;
const SAVE_DATA = navigator.connection?.saveData === true;
const mounted = new WeakMap();
let motionPromise;

function loadMotion() {
  if (!motionPromise) {
    motionPromise = import('./yc-fruit-slice-nav-D3rMpp9a.532f63aefc.js')
      .then(({ n: ScrollTrigger, r: gsap }) => {
        if (gsap && ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
        return { gsap, ScrollTrigger };
      })
      .catch((error) => {
        console.warn('Yunicrafts homepage motion is unavailable', error);
        return { gsap: null, ScrollTrigger: null };
      });
  }
  return motionPromise;
}

function normalizeAddUrl(action) {
  const url = new URL(action || '/cart/add.js', window.location.origin);
  if (!url.pathname.endsWith('.js')) {
    url.pathname = url.pathname.replace(/\/cart\/add$/, '/cart/add.js');
  }
  return url.toString();
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
    return;
  }

  const trigger = document.querySelector('[data-testid="cart-drawer-trigger"], [aria-controls*="cart"]');
  if (trigger instanceof HTMLElement) trigger.click();
}

function getCartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
}

function bindQuickAdd(root) {
  root.querySelectorAll('.yc-home-add-form').forEach((form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.ycBound === 'true') return;
    form.dataset.ycBound = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.ycSubmitting === 'true') return;
      form.dataset.ycSubmitting = 'true';
      const button = form.querySelector('.yc-home-quick-add');
      const originalLabel = button?.textContent || '';
      form.querySelector('[data-yc-cart-error]')?.remove();
      const formData = new FormData(form);
      const sectionIds = getCartSectionIds();
      if (sectionIds.length > 0) {
        formData.set('sections', sectionIds.join(','));
        formData.set('sections_url', window.location.pathname);
      }
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 8000);

      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingLabel || 'Adding';
      }

      try {
        const response = await fetch(normalizeAddUrl(form.action), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw data;

        const quantity = Number(formData.get('quantity')) || 1;
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'yc-homepage',
            // This value is the incremental quantity, matching the theme's
            // product-form cart-bubble contract rather than an absolute count.
            data: { source: 'product-form-component', itemCount: quantity, sections: data.sections || {} },
          },
        }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: data }));
        openCartDrawer();
        if (button instanceof HTMLButtonElement) {
          button.textContent = button.dataset.successLabel || 'Added';
          window.setTimeout(() => { button.textContent = originalLabel; }, 1600);
        }
      } catch (error) {
        console.warn('Yunicrafts homepage quick add needs review', error);
        if (button instanceof HTMLButtonElement) {
          button.textContent = button.dataset.errorLabel || 'Try again';
        }
        if (Number(error?.status) >= 400 && Number(error?.status) < 500) {
          let status = form.querySelector('[data-yc-cart-error]');
          if (!status) {
            status = document.createElement('p');
            status.dataset.ycCartError = 'true';
            status.className = 'yc-home-cart-error';
            status.setAttribute('role', 'alert');
            form.append(status);
          }
          status.textContent = error.description || error.message || button?.dataset.errorLabel || originalLabel;
        } else {
          const cartUrl = new URL(form.action, window.location.origin);
          cartUrl.pathname = cartUrl.pathname.replace(/\/cart\/add(?:\.js)?$/, '/cart');
          cartUrl.search = '';
          window.location.assign(cartUrl.toString());
        }
      } finally {
        form.dataset.ycSubmitting = 'false';
        window.clearTimeout(timeoutId);
        if (button instanceof HTMLButtonElement) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    });
  });
}

function bindUgc(root) {
  root.querySelectorAll('[data-yc-ugc-card]').forEach((card) => {
    const video = card.querySelector('[data-yc-ugc-video]');
    const button = card.querySelector('[data-yc-ugc-toggle]');
    if (!(video instanceof HTMLVideoElement) || !(button instanceof HTMLButtonElement) || button.dataset.ycBound === 'true') return;
    button.dataset.ycBound = 'true';
    const syncPlayback = () => {
      const playing = !video.paused && !video.ended;
      card.dataset.playing = String(playing);
      button.textContent = playing ? 'Ⅱ' : '▶';
      button.setAttribute('aria-label', playing ? button.dataset.pauseLabel || 'Pause video' : button.dataset.playLabel || 'Play video');
      button.setAttribute('aria-pressed', String(playing));
    };
    video.addEventListener('play', syncPlayback);
    video.addEventListener('pause', syncPlayback);
    video.addEventListener('ended', syncPlayback);

    button.addEventListener('click', async () => {
      try {
        if (video.paused) {
          root.querySelectorAll('[data-yc-ugc-video]').forEach((otherVideo) => {
            if (otherVideo instanceof HTMLVideoElement && otherVideo !== video) otherVideo.pause();
          });
          video.muted = false;
          await video.play();
        } else {
          video.pause();
        }
        syncPlayback();
      } catch (error) {
        console.warn('Yunicrafts UGC video could not start', error);
      }
    });
  });
}

function bindHeroVideo(root) {
  root.querySelectorAll('[data-yc-hero-video]').forEach((video) => {
    if (!(video instanceof HTMLVideoElement) || video.dataset.ycBound === 'true') return;
    video.dataset.ycBound = 'true';
    const markReady = () => video.classList.add('is-ready');
    video.addEventListener('loadeddata', markReady, { once: true });
    video.addEventListener('canplay', markReady, { once: true });
    if (REDUCED_MOTION || SAVE_DATA || IS_MOBILE) return;
    const isVisible = () => {
      const style = window.getComputedStyle(video);
      return style.display !== 'none' && style.visibility !== 'hidden' && video.getBoundingClientRect().width > 0;
    };

    const start = () => {
      if (!video.isConnected || !isVisible()) return;
      video.load();
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 2200 });
    } else {
      window.setTimeout(start, 900);
    }
  });
}

function applyMotion(root, gsap, ScrollTrigger) {
  if (!gsap) return () => {};
  const context = gsap.context(() => {
    const hero = root.querySelector('.yc-home-hero');
    if (hero) {
      gsap.from(hero.querySelectorAll('[data-yc-hero-reveal]'), {
        y: 16,
        duration: 0.72,
        stagger: 0.08,
        ease: 'power3.out',
        delay: 0.08,
      });

      const heroMedia = hero.querySelector('[data-yc-hero-media]');
      if (heroMedia && ScrollTrigger) {
        gsap.to(heroMedia, {
          yPercent: 6,
          ease: 'none',
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 1 },
        });
      }
    }

    root.querySelectorAll('[data-yc-reveal]').forEach((element) => {
      gsap.from(element, {
        y: 30,
        autoAlpha: 0,
        duration: 0.72,
        ease: 'power3.out',
        scrollTrigger: ScrollTrigger ? { trigger: element, start: 'top 84%', once: true } : undefined,
      });
    });
  }, root);

  return () => context.revert();
}

function scheduleMotion(root, record) {
  // Mobile keeps the lightweight CSS transitions; do not pull the desktop GSAP bundle over a constrained connection.
  if (REDUCED_MOTION || IS_MOBILE || SAVE_DATA) return;
  const run = () => {
    if (record.cancelled || !root.isConnected) return;
    loadMotion().then(({ gsap, ScrollTrigger }) => {
      if (record.cancelled || !root.isConnected) return;
      record.cleanup = applyMotion(root, gsap, ScrollTrigger);
    });
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 1200 });
  } else {
    window.setTimeout(run, 320);
  }
}

function mount(section) {
  if (!(section instanceof HTMLElement) || mounted.has(section)) return;
  bindQuickAdd(section);
  bindUgc(section);
  bindHeroVideo(section);
  const record = { cancelled: false, cleanup: null };
  mounted.set(section, record);
  scheduleMotion(section, record);
}

function unmount(section) {
  const record = mounted.get(section);
  if (record) {
    record.cancelled = true;
    if (typeof record.cleanup === 'function') record.cleanup();
  }
  mounted.delete(section);
}

function mountAll(root = document) {
  if (root instanceof HTMLElement && root.matches('[data-yc-homepage]')) mount(root);
  root.querySelectorAll?.('[data-yc-homepage]').forEach(mount);
}

function boot() {
  mountAll();
  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) {
      event.target.querySelectorAll('[data-yc-homepage]').forEach(unmount);
      if (event.target.matches('[data-yc-homepage]')) unmount(event.target);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
